import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, ArrowRight, CheckCircle } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Reveal } from '@/components/Reveal';
import { SEOHead } from '@/components/SEOHead';
import { servicePillars } from '@/data/servicePillars';

export default function PillarDetail({ pillar }) {
  const Icon = pillar.icon;
  const related = servicePillars.filter(p => p.slug !== pillar.slug).slice(0, 3);

  return (
    <div data-testid={`pillar-detail-${pillar.slug}`}>
      <SEOHead
        title={pillar.seoTitle}
        description={pillar.seoDesc}
        canonical={`/services/${pillar.slug}`}
      />

      {/* ── Hero ── */}
      <section
        className="relative pt-28 pb-20 overflow-hidden"
        style={{ background: 'linear-gradient(135deg, #0A1628 0%, #153E5C 100%)' }}
      >
        <div className="absolute inset-0 pointer-events-none" style={{
          backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.015) 1px, transparent 0)',
          backgroundSize: '40px 40px'
        }} />
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#D4AF37]/20 to-transparent" />

        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.4 }}
            className="flex items-center gap-2 text-xs text-white/35 font-sans mb-10">
            <Link to="/" className="hover:text-white/60 transition-colors duration-150">Home</Link>
            <span className="text-white/20">/</span>
            <Link to="/services" className="hover:text-white/60 transition-colors duration-150">Services</Link>
            <span className="text-white/20">/</span>
            <span className="text-white/55">{pillar.title}</span>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 28 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}>
            <Link to="/services"
              className="inline-flex items-center gap-2 text-teal-400 hover:text-teal-300 text-sm mb-8 transition-colors duration-150">
              <ArrowLeft className="w-4 h-4" /> All Services
            </Link>

            <div className="flex items-start gap-6 mb-8">
              <div className="w-16 h-16 bg-[#0E5A57] ring-1 ring-white/10 rounded-2xl flex items-center justify-center flex-shrink-0">
                <Icon className="w-8 h-8 text-white" />
              </div>
              <div>
                <p className="overline-label text-[#D4AF37] mb-2">NKP VENTURES Service Pillar</p>
                <h1 className="font-serif text-4xl lg:text-5xl font-bold text-white leading-tight">
                  {pillar.title}
                </h1>
              </div>
            </div>

            <p className="text-white/55 text-lg font-sans leading-relaxed max-w-2xl">{pillar.tagline}</p>
          </motion.div>
        </div>
      </section>

      {/* ── Overview ── */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 lg:gap-20">
            <Reveal className="lg:col-span-3">
              <p className="overline-label text-[#0E5A57] mb-4">Overview</p>
              <h2 className="font-serif text-3xl font-bold text-[#153E5C] mb-6 leading-tight">What this service covers</h2>
              <p className="text-[#374151] text-base leading-relaxed font-sans">{pillar.description}</p>
            </Reveal>

            <Reveal delay={0.12} className="lg:col-span-2">
              <div className="p-8 bg-[#F7FAFC] border border-[#E5E7EB] rounded-2xl">
                <p className="overline-label text-[#D4AF37] mb-5">Services Included</p>
                <ul className="space-y-2.5">
                  {pillar.services.map(s => (
                    <li key={s} className="flex items-start gap-3">
                      <CheckCircle className="w-4 h-4 text-[#0E5A57] flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-[#374151] font-sans leading-relaxed">{s}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ── Marketplace logos (E-commerce pillar only) ── */}
      {pillar.marketplaces && (
        <section className="py-16 bg-[#F7FAFC] border-t border-b border-[#E5E7EB]">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <Reveal>
              <p className="overline-label text-[#0E5A57] mb-6 text-center">Platforms We Support</p>
              <div className="flex flex-wrap justify-center gap-3">
                {pillar.marketplaces.map(mp => (
                  <span key={mp}
                    className="px-5 py-2.5 bg-white border border-[#E5E7EB] rounded-full text-sm font-semibold text-[#153E5C] font-sans shadow-sm">
                    {mp}
                  </span>
                ))}
              </div>
              <p className="text-xs text-[#9CA3AF] text-center mt-5 font-sans max-w-lg mx-auto">
                NKP VENTURES provides operational support for sellers on these platforms. We are not an officially authorised partner of any marketplace or platform listed above.
              </p>
            </Reveal>
          </div>
        </section>
      )}

      {/* ── Business Outcomes ── */}
      <section className="py-24" style={{ background: '#0D1F30' }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <Reveal>
            <div className="flex items-center gap-3 mb-4">
              <span className="w-6 h-px bg-[#D4AF37]" />
              <p className="overline-label text-[#D4AF37]">Business Outcomes</p>
            </div>
            <h2 className="font-serif text-3xl lg:text-4xl font-bold text-white mb-4 leading-tight">What you can expect</h2>
            <p className="text-white/45 mb-14 font-sans max-w-2xl leading-relaxed">
              The tangible results that flow from professional, consistent operational support in this area.
            </p>
          </Reveal>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {pillar.benefits.map((b, i) => (
              <Reveal key={b} delay={i * 0.07}>
                <div className="group p-7 border border-white/8 rounded-2xl hover:border-white/15 hover:bg-white/4 transition-all duration-300">
                  <div className="w-8 h-0.5 bg-gradient-to-r from-[#0E5A57] to-[#D4AF37] rounded mb-5" />
                  <p className="text-white/75 font-sans text-sm leading-relaxed">{b}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── Use Cases ── */}
      <section className="py-24 bg-[#F7FAFC]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <Reveal>
            <p className="overline-label text-[#0E5A57] mb-4">Typical Use Cases</p>
            <h2 className="font-serif text-3xl lg:text-4xl font-bold text-[#153E5C] mb-12 max-w-2xl leading-tight">
              Who this service is built for
            </h2>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {pillar.useCases.map((uc, i) => (
              <Reveal key={i} delay={i * 0.05}>
                <div className="flex gap-5 p-6 bg-white border border-[#E5E7EB] rounded-xl hover:border-[#0E5A57]/20 transition-colors duration-200">
                  <div className="flex-shrink-0 w-8 h-8 bg-[#0E5A57]/8 rounded-lg flex items-center justify-center mt-0.5">
                    <span className="font-mono text-xs font-bold text-[#0E5A57]">{String(i + 1).padStart(2, '0')}</span>
                  </div>
                  <p className="text-sm text-[#374151] font-sans leading-relaxed">{uc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      {pillar.faqs && pillar.faqs.length > 0 && (
        <section className="py-24 bg-white">
          <div className="max-w-3xl mx-auto px-6 lg:px-8">
            <Reveal>
              <p className="overline-label text-[#0E5A57] mb-4">Common Questions</p>
              <h2 className="font-serif text-3xl font-bold text-[#153E5C] mb-12 leading-tight">Frequently asked questions</h2>
            </Reveal>
            <Accordion type="single" collapsible className="space-y-2">
              {pillar.faqs.map((faq, i) => (
                <Reveal key={i} delay={i * 0.05}>
                  <AccordionItem value={`faq-${i}`} className="bg-[#F7FAFC] border border-[#E5E7EB] rounded-xl px-6">
                    <AccordionTrigger className="font-sans font-medium text-[#1F2937] text-left py-5 hover:no-underline hover:text-[#0E5A57] text-sm">
                      {faq.q}
                    </AccordionTrigger>
                    <AccordionContent className="text-[#6B7280] text-sm leading-relaxed font-sans pb-5">
                      {faq.a}
                    </AccordionContent>
                  </AccordionItem>
                </Reveal>
              ))}
            </Accordion>
          </div>
        </section>
      )}

      {/* ── Related Pillars ── */}
      <section className="py-20 bg-[#F7FAFC] border-t border-[#E5E7EB]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <Reveal>
            <p className="overline-label text-[#0E5A57] mb-8">Explore More Service Pillars</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {related.map(p => {
                const RelIcon = p.icon;
                return (
                  <Link key={p.slug} to={`/services/${p.slug}`}
                    className="group flex items-center gap-4 p-5 bg-white border border-[#E5E7EB] rounded-xl hover:border-[#0E5A57]/25 hover:bg-white hover:shadow-md transition-all duration-200">
                    <div className="w-10 h-10 bg-white border border-[#E5E7EB] rounded-xl flex items-center justify-center flex-shrink-0 group-hover:bg-[#0E5A57] group-hover:border-[#0E5A57] transition-all duration-300">
                      <RelIcon className="w-5 h-5 text-[#0E5A57] group-hover:text-white transition-colors duration-300" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-[#153E5C] group-hover:text-[#0E5A57] transition-colors duration-200 leading-snug">{p.shortTitle}</p>
                      <p className="text-xs text-[#9CA3AF] mt-0.5 truncate">{p.tagline}</p>
                    </div>
                    <ArrowRight className="w-4 h-4 text-[#D1D5DB] group-hover:text-[#0E5A57] flex-shrink-0 transition-colors duration-200" />
                  </Link>
                );
              })}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="py-28 bg-[#0E5A57]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <Reveal>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <p className="overline-label text-teal-300 mb-4">Ready to Get Started?</p>
                <h2 className="font-serif text-4xl font-bold text-white mb-4 leading-tight">
                  Speak with our<br />{pillar.shortTitle} team.
                </h2>
                <p className="text-white/60 font-sans leading-relaxed max-w-md">
                  Schedule a no-obligation consultation to discuss how we can support your specific operational needs.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 lg:justify-end">
                <Link to="/contact"
                  className="inline-flex items-center justify-center gap-2 bg-[#D4AF37] hover:bg-[#E8CA5A] text-[#153E5C] font-bold px-9 py-4 rounded-full transition-all duration-200 group">
                  Schedule a Consultation
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform duration-200" />
                </Link>
                <Link to="/services"
                  className="inline-flex items-center justify-center gap-2 border border-white/25 hover:border-white/50 text-white font-medium px-9 py-4 rounded-full transition-all duration-200 text-sm">
                  <ArrowLeft className="w-4 h-4" /> All Services
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
